import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import * as z from "zod/v4";
import { toast } from "sonner";
import { HTTPError } from "ky";
import { api } from "../http";
import { paginatedListSchema } from "../utils/zod";
import {
  isZoomConnectionRequiredError,
  showZoomConnectionRequiredToast,
} from "../utils/zoom-connection";
import {
  participantDetailQueryOptions,
  participantSchema,
} from "./participants";
import { invalidateBillingUsage } from "./billing";

export type MeetingsSortField = "created_at" | "meeting_start_time";
export type MeetingsSortOrder = "asc" | "desc";
export type MeetingScope = "own" | "organization";

export type MeetingsListParams = {
  page?: number;
  per_page?: number;
  search?: string;
  date_filter?: string;
  date_from?: string;
  date_to?: string;
  date_direction?: "past" | "future";
  start_date?: string;
  end_date?: string;
  state_group?: Array<string>;
  severity?: Array<string>;
  platform?: Array<string>;
  sort_by?: MeetingsSortField;
  sort_order?: MeetingsSortOrder;
  scope?: MeetingScope;
};

const SEVERITY_FILTER_TO_BACKEND: Record<string, string> = {
  Critical: "high",
  Warning: "medium",
  Safe: "low",
};

export type SharedMeetingsListParams = {
  page?: number;
  per_page?: number;
  search?: string;
  date_filter?: string;
  date_from?: string;
  date_to?: string;
  state_group?: Array<string>;
  severity?: Array<string>;
};

export type MaskedEntity = {
  entity_name: string;
  entity_text: string;
  masked_entity_text: string;
};

export type Citation = {
  utterance_index: number;
  /** Absolute ISO timestamp, despite the name. Null when the API cannot resolve it. */
  start_time_ms: string | null;
  duration_ms: number | null;
};

export type SectionPoint = {
  text: string;
  citations: Array<Citation>;
};

export type Section = {
  title: string;
  points: Array<SectionPoint>;
};

export type ActionItem = {
  owner: string;
  task: string;
  due_date: string | null;
  citations: Array<Citation>;
};

export type MeetingSummaryResponseV1 = {
  version: 1;
};

export type SummaryBody = {
  summary_text: string;
  sections: Array<Section>;
  action_items: Array<ActionItem>;
  masked_entities: {
    summary_masked_text: string;
    sections_masked: Array<Section>;
    action_items_masked: Array<ActionItem>;
    entities_list: Array<MaskedEntity>;
  };
};

export type MeetingSummaryResponseV2 = {
  id: string;
  meeting_bot_id: string;
  version: 2;
  detail: string;
  created_at: string;
  updated_at: string | null;
} & (
  | {
      status: "COMPLETED";
      body: SummaryBody;
    }
  | {
      status: "MEETING_NOT_ENDED" | "GENERATING" | "NO_CONTENT" | "FAILED";
      body: null;
    }
);

export type MeetingSummaryResponse =
  | MeetingSummaryResponseV1
  | MeetingSummaryResponseV2;

function getDateRange(
  dateFilter: string | undefined,
  dateFrom?: string,
  dateTo?: string,
): {
  start_date?: string;
  end_date?: string;
} {
  if (!dateFilter || dateFilter === "all") return {};

  if (dateFilter === "custom") {
    if (!dateFrom || !dateTo) return {};
    const start = new Date(dateFrom);
    start.setHours(0, 0, 0, 0);
    const end = new Date(dateTo);
    end.setHours(23, 59, 59, 999);
    return { start_date: start.toISOString(), end_date: end.toISOString() };
  }

  const now = new Date();
  const endDate = now.toISOString();
  let startDate: Date;

  switch (dateFilter) {
    case "today":
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case "week":
      startDate = new Date(now);
      startDate.setDate(now.getDate() - 7);
      break;
    case "month":
      startDate = new Date(now);
      startDate.setMonth(now.getMonth() - 1);
      break;
    case "year":
      startDate = new Date(now);
      startDate.setFullYear(now.getFullYear() - 1);
      break;
    default:
      return {};
  }

  return {
    start_date: startDate.toISOString(),
    end_date: endDate,
  };
}

function getFutureDateRange(
  dateFilter: string | undefined,
  dateFrom: string | undefined,
  dateTo: string | undefined,
): { start_date?: string; end_date?: string } {
  if (!dateFilter || dateFilter === "all") return {};

  if (dateFilter === "custom") {
    if (!dateFrom || !dateTo) return {};
    const start = new Date(dateFrom);
    start.setHours(0, 0, 0, 0);
    const end = new Date(dateTo);
    end.setHours(23, 59, 59, 999);
    return { start_date: start.toISOString(), end_date: end.toISOString() };
  }

  const now = new Date();
  const end = new Date(now);

  switch (dateFilter) {
    case "today":
      end.setDate(now.getDate() + 1);
      break;
    case "week":
      end.setDate(now.getDate() + 7);
      break;
    case "month":
      end.setMonth(now.getMonth() + 1);
      break;
    default:
      return {};
  }

  return { start_date: now.toISOString(), end_date: end.toISOString() };
}

function buildMeetingsSearchParams(params: {
  page: number;
  per_page: number;
  search?: string;
  date_filter?: string;
  date_from?: string;
  date_to?: string;
  date_direction?: "past" | "future";
  start_date?: string;
  end_date?: string;
  state_group?: Array<string>;
  severity?: Array<string>;
  platform?: Array<string>;
  sort_by?: MeetingsSortField;
  sort_order?: MeetingsSortOrder;
  scope?: MeetingScope;
}): URLSearchParams {
  const searchParams = new URLSearchParams({
    page: params.page.toString(),
    per_page: params.per_page.toString(),
    include_analytics: "true",
  });

  if (params.search) searchParams.set("search", params.search);
  if (params.scope) searchParams.set("scope", params.scope);

  if (params.sort_by) {
    searchParams.set("sort_by", params.sort_by);
    searchParams.set("sort_order", params.sort_order ?? "desc");
  }

  if (params.state_group && !params.state_group.includes("All")) {
    params.state_group.forEach((g) => searchParams.append("state_group", g));
  }
  const severityGroups = (params.severity ?? [])
    .filter((severity) => severity && severity !== "All")
    .map((severity) => SEVERITY_FILTER_TO_BACKEND[severity])
    .filter((severity): severity is string => Boolean(severity));
  for (const severity of severityGroups) {
    searchParams.append("severity", severity);
  }

  if (params.platform)
    params.platform.forEach((p) => searchParams.append("platform", p));

  // Explicit start/end dates take precedence over the preset date_filter.
  if (params.start_date || params.end_date) {
    if (params.start_date) searchParams.set("start_date", params.start_date);
    if (params.end_date) searchParams.set("end_date", params.end_date);
  } else {
    // Resolve the relative preset at request time so the query key stays stable.
    const { start_date, end_date } =
      params.date_direction === "future"
        ? getFutureDateRange(
            params.date_filter,
            params.date_from,
            params.date_to,
          )
        : getDateRange(params.date_filter, params.date_from, params.date_to);
    if (start_date) searchParams.set("start_date", start_date);
    if (end_date) searchParams.set("end_date", end_date);
  }

  return searchParams;
}

export function meetingsListQueryOptions(params: MeetingsListParams = {}) {
  const { page = 1, per_page = 10, ...rest } = params;

  return queryOptions({
    queryKey: ["meetings", "list", params],
    async queryFn() {
      const searchParams = buildMeetingsSearchParams({
        page,
        per_page,
        ...rest,
      });
      const url = `api/v1/meetings/?${searchParams.toString()}`;
      return api.processing.raw.get(url).json<MeetingBotList>();
    },
  });
}

export function sharedMeetingsListQueryOptions(
  params: SharedMeetingsListParams = {},
) {
  const { page = 1, per_page = 10, ...rest } = params;

  return queryOptions({
    queryKey: ["meetings", "shared-with-me", params],
    async queryFn() {
      const searchParams = buildMeetingsSearchParams({
        page,
        per_page,
        ...rest,
      });
      const url = `api/v1/meetings/shared-with-me?${searchParams.toString()}`;
      return parseSharedMeetingsList(await api.processing.raw.get(url).json());
    },
  });
}

const sharedMeetingSchema = z.object({
  id: z.uuid(),
  meeting_id: z.uuid(),
  meeting_name: z.string(),
  meeting_url: z.url(),
  platform: z.enum(["google", "teams", "zoom"]),
  shared_by_user_id: z.uuid(),
  shared_by_name: z.string().nullable(),
  can_view_recording: z.boolean(),
  can_view_transcription: z.boolean(),
  can_view_analysis: z.boolean(),
  can_view_summary: z.boolean(),
  shared_at: z.iso.datetime(),
  meeting_state: z.string(),
  meeting_start_time: z.iso.datetime().nullable(),
});

const sharedMeetingListSchema = paginatedListSchema(sharedMeetingSchema);

export type SharedMeeting = z.infer<typeof sharedMeetingSchema>;
export type SharedMeetingList = z.infer<typeof sharedMeetingListSchema>;

export function mapSharedMeetingStateToStateGroup(
  meetingState: string,
): MeetingBot["state_group"] {
  const normalized = meetingState
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

  switch (normalized) {
    case "SCHEDULED":
      return "SCHEDULED";
    case "PREPARATION":
    case "JOINING":
    case "PREPARING":
      return "PREPARATION";
    case "ACTIVE":
    case "LIVE":
    case "IN_PROGRESS":
    case "INPROGRESS":
      return "ACTIVE";
    case "ENDED":
    case "COMPLETED":
    case "COMPLETE":
      return "ENDED";
    case "ERROR":
    case "FAILED":
      return "ERROR";
    case "POST_PROCESSING":
    case "PROCESSING":
      return "POST_PROCESSING";
    default:
      return "PREPARATION";
  }
}

function parseSharedMeetingsList(raw: unknown): SharedMeetingList {
  const parsedList = sharedMeetingListSchema.safeParse(raw);
  if (parsedList.success) {
    return parsedList.data;
  }

  if (Array.isArray(raw)) {
    const items = raw
      .map((entry) => sharedMeetingSchema.safeParse(entry))
      .filter((result) => result.success)
      .map((result) => result.data);

    return {
      count: items.length,
      items,
      next_page: null,
      previous_page: null,
    };
  }

  if (typeof raw === "object" && raw !== null) {
    const record = raw as Record<string, unknown>;
    const rawItems = Array.isArray(record.items)
      ? record.items
      : Array.isArray(record.results)
        ? record.results
        : [];

    const items = rawItems
      .map((entry) => sharedMeetingSchema.safeParse(entry))
      .filter((result) => result.success)
      .map((result) => result.data);

    return {
      count: typeof record.count === "number" ? record.count : items.length,
      items,
      next_page: typeof record.next_page === "string" ? record.next_page : null,
      previous_page:
        typeof record.previous_page === "string" ? record.previous_page : null,
    };
  }

  return {
    count: 0,
    items: [],
    next_page: null,
    previous_page: null,
  };
}

export const featureSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  is_active: z.boolean(),
  category: z.string(),
  label: z.string(),
  settings: z.record(z.string(), z.unknown()),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});

export type Feature = z.infer<typeof featureSchema>;

const FEATURE_DISPLAY_ORDER = [
  "pii",
  "voice_authentication",
  "ai_content_detection",
] as const;

export function sortFeaturesByDisplayOrder<T extends { label: string }>(
  features: Array<T>,
): Array<T> {
  const order = new Map<string, number>(
    FEATURE_DISPLAY_ORDER.map((label, index) => [label, index]),
  );
  return [...features].sort(
    (a, b) =>
      (order.get(a.label) ?? FEATURE_DISPLAY_ORDER.length) -
      (order.get(b.label) ?? FEATURE_DISPLAY_ORDER.length),
  );
}

export const meetingBotSchema = z.object({
  id: z.uuid(),
  calendar_id: z.string(),
  external_id: z.string(),
  meeting_name: z.string(),
  meeting_url: z.url(),
  bot_name: z.string(),
  platform: z.enum(["google", "teams", "zoom"]),
  entry_announcement: z.boolean(),
  state: z.string(),
  state_group: z.enum([
    "SCHEDULED",
    "PREPARATION",
    "ACTIVE",
    "ENDED",
    "ERROR",
    "POST_PROCESSING",
  ]),
  leave_reason: z.string().nullable(),
  auto_leave_on_silence: z.boolean(),
  join_at: z.iso.datetime().nullable(),
  meeting_start_time: z.iso.datetime().nullable(),
  left_at: z.iso.datetime().nullable().optional(),
  error_reason: z.string().nullable().optional(),
  features: z.array(featureSchema),
  participants: z.array(participantSchema).nullable(),
  analytics: z
    .object({
      ai_content_detection: z.object({
        ai: z.number().nullable(),
        human: z.number().nullable(),
      }),
      pii: z.object({
        count: z.number(),
        by_type: z.record(z.string(), z.number()),
      }),
      voice_authentication: z.object({
        real_ratio: z.number().nullable(),
        fake_ratio: z.number().nullable(),
      }),
      risk_score: z.object({
        total: z.number().nullable(),
        ai_component: z.number().nullable(),
        pii_component: z.number().nullable(),
        vexon_component: z.number().nullable(),
        data_sources: z.array(z.string()),
      }),
    })
    .nullable(),
  recording: z
    .object({
      url: z.string(),
      start_time: z.iso.datetime(),
    })
    .nullable(),
  created_by_user_id: z.uuid(),
  created_at: z.iso.datetime(),
  summary: z.string().nullable().optional(),

  permissions: z.object({
    can_view_transcription: z.boolean(),
    can_view_recording: z.boolean(),
    can_view_analysis: z.boolean(),
    can_view_summary: z.boolean(),
  }),
});

const meetingBotListSchema = paginatedListSchema(meetingBotSchema);

export type MeetingBot = z.infer<typeof meetingBotSchema>;
export type MeetingBotList = z.infer<typeof meetingBotListSchema>;

export function meetingBotQueryOptions(
  meetingBotId: string,
  opts: {
    include_participants?: boolean;
    /** @deprecated it is always true on the backend now and setting this has no effect */
    include_analytics?: boolean;
  } = {},
) {
  const searchParams = new URLSearchParams();
  if (opts.include_participants)
    searchParams.set("include_participants", "true");
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { include_analytics, ...effectiveOpts } = opts;

  return queryOptions({
    queryKey: ["meetings", meetingBotId, effectiveOpts],
    queryFn({ client }) {
      const resPromise = api.processing.raw
        .get(`api/v1/meetings/${meetingBotId}`, {
          searchParams,
        })
        .json<MeetingBot>();

      // cache participants since it's the same data
      resPromise.then((res) => {
        if (res.participants) {
          res.participants.forEach((p) => {
            client.setQueryData(
              participantDetailQueryOptions(p.id).queryKey,
              p,
            );
          });
        }
      });

      if (opts.include_participants) {
        // populate non-participants cache since that's a subset of this
        resPromise.then((res) => {
          client.setQueryData(
            meetingBotQueryOptions(meetingBotId, {
              include_participants: false,
            }).queryKey,
            res,
          );
        });
      }

      return resPromise;
    },
  });
}

export function meetingSummaryQueryOptions(meetingBotId: string) {
  return queryOptions({
    queryKey: ["meetings", meetingBotId, "summary"],
    queryFn: () =>
      api.processing.raw
        .get(`api/v1/meetings/${meetingBotId}/summary`)
        .json<MeetingSummaryResponse>(),
  });
}

const featuresListSchema = paginatedListSchema(featureSchema);
export type FeaturesList = z.infer<typeof featuresListSchema>;

export function featuresQueryOptions() {
  return queryOptions({
    queryKey: ["features"],
    queryFn: () =>
      api.processing.raw.get("api/v1/features/").json<FeaturesList>(),
  });
}

export type PiiEntity = {
  id: string;
  name: string;
  description: string;
};

export function featureByLabelQueryOptions(featureLabel: string) {
  return queryOptions({
    queryKey: ["features", "by-label", featureLabel],
    queryFn: () =>
      api.processing.raw
        .get(`api/v1/features/by-label/${encodeURIComponent(featureLabel)}`)
        .json<Feature>(),
  });
}

export function piiEntitiesQueryOptions() {
  return queryOptions({
    queryKey: ["features", "pii-entities"],
    queryFn: () =>
      api.processing.raw
        .get("api/v1/features/pii-entities")
        .json<Array<PiiEntity>>(),
  });
}

export type PiiSeverity = "CRITICAL" | "CONFIDENTIAL";

export type StandardPiiEntity = {
  entity_id: string;
  name: string;
  description: string;
  severity: PiiSeverity;
  is_enabled: boolean;
};

export type CustomPiiEntity = {
  id: string;
  label: string;
  text: string;
  replacement_text: string;
  severity: PiiSeverity;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
};

export function standardPiiQueryOptions() {
  return queryOptions({
    queryKey: ["pii", "standard"],
    queryFn: () =>
      api.processing.raw
        .get("api/v1/pii/standard/")
        .json<Array<StandardPiiEntity>>(),
  });
}

export function customPiiQueryOptions() {
  return queryOptions({
    queryKey: ["pii", "custom"],
    queryFn: () =>
      api.processing.raw
        .get("api/v1/pii/custom/")
        .json<Array<CustomPiiEntity>>(),
  });
}

export type UpdateFeaturePayload = {
  is_active?: boolean;
  settings?: Record<string, unknown>;
};

export function useUpdateFeatureMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      featureId,
      payload,
    }: {
      featureId: string;
      payload: UpdateFeaturePayload;
    }) => {
      const response = await api.processing.raw
        .patch(`api/v1/features/${featureId}`, {
          json: payload,
        })
        .json<Feature>();
      return response;
    },
    onSuccess: async () => {
      toast.success("Feature updated successfully!");
      options?.onSuccess?.();
      await queryClient.invalidateQueries({ queryKey: ["features"] });
    },
    async onError(error) {
      if (error instanceof HTTPError) {
        try {
          const body = await error.response.json();
          if (body.detail) {
            toast.error(body.detail);
            options?.onError?.(error);
            return;
          }
        } catch {
          // ignore json parse errors
        }
      }
      toast.error("Failed to update feature");
      options?.onError?.(error);
    },
  });
}

export function useCancelMeetingMutation(options?: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (meetingId: string) => {
      await api.processing.raw.delete(`api/v1/meetings/${meetingId}/cancel`);
    },
    onSuccess: async () => {
      toast.success("Meeting cancelled successfully!");
      options?.onSuccess?.();
      await queryClient.invalidateQueries({
        queryKey: ["meetings"],
      });
      await invalidateBillingUsage(queryClient);
    },
    async onError(error) {
      if (error instanceof HTTPError) {
        try {
          const body = await error.response.json();
          if (body.detail) {
            toast.error(body.detail);
            return;
          }
        } catch {
          // ignore json parse errors
        }
      }
      toast.error("Failed to cancel meeting");
    },
  });
}

export function useLeaveMeetingMutation(options?: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (meetingBotId: string) => {
      await api.processing.raw.post(`api/v1/meetings/${meetingBotId}/leave`);
    },
    onSuccess: async () => {
      toast.success("Bot removed successfully!");
      options?.onSuccess?.();
      await queryClient.invalidateQueries({
        queryKey: ["meetings"],
      });
      await invalidateBillingUsage(queryClient);
    },
    async onError(error) {
      if (error instanceof HTTPError) {
        try {
          const body = await error.response.json();
          if (body.detail) {
            toast.error(body.detail);
            return;
          }
        } catch {
          // ignore json parse errors
        }
      }
      toast.error("Failed to remove agent");
    },
  });
}

export function useDeleteMeetingMutation(options?: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (meetingBotId: string) => {
      await api.processing.raw.delete(`api/v1/meetings/${meetingBotId}`);
    },
    onSuccess: async () => {
      toast.success("Meeting deleted successfully!");
      options?.onSuccess?.();
      await queryClient.invalidateQueries({
        queryKey: ["meetings"],
      });
    },
    async onError(error) {
      if (error instanceof HTTPError) {
        try {
          const body = await error.response.json();
          if (body.detail) {
            toast.error(body.detail);
            return;
          }
        } catch {
          // ignore json parse errors
        }
      }
      toast.error("Failed to delete meeting");
    },
  });
}

export function useRejoinMeetingMutation(options?: {
  onSuccess?: (meeting: MeetingBot) => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (meetingBotId: string) => {
      const response = await api.processing.raw
        .post(`api/v1/meetings/${meetingBotId}/rejoin`)
        .json<MeetingBot>();
      return response;
    },
    onSuccess: async (meeting) => {
      toast.success("Rejoin started");
      options?.onSuccess?.(meeting);
      await queryClient.invalidateQueries({ queryKey: ["meetings"] });
    },
    async onError(error) {
      if (isZoomConnectionRequiredError(error)) {
        await showZoomConnectionRequiredToast(error);
        options?.onError?.(error);
        return;
      }
      if (error instanceof HTTPError) {
        try {
          const body = await error.response.json();
          if (body.detail) {
            toast.error(body.detail);
            options?.onError?.(error);
            return;
          }
        } catch {
          // ignore json parse errors
        }
      }
      toast.error("Failed to rejoin meeting");
      options?.onError?.(error);
    },
  });
}

export type MeetingSharePermissions = {
  can_view_recording: boolean;
  can_view_transcription: boolean;
  can_view_analysis: boolean;
  can_view_summary: boolean;
};

export type MeetingShare = {
  id: string;
  email: string;
  full_name?: string | null;
  permissions: MeetingSharePermissions;
};

export type CreateMeetingShareRecipient = {
  email: string;
  permissions: MeetingSharePermissions;
};

export type CreateMeetingShareResult = {
  email: string;
  isSuccess: boolean;
  errorMessage: string | null;
  shareId: string | null;
};

function normalizeCreateShareResult(
  raw: unknown,
): CreateMeetingShareResult | null {
  if (typeof raw !== "object" || raw === null) return null;
  const entry = raw as Record<string, unknown>;

  const email = pickString(entry, [
    "email",
    "shared_email",
    "recipient_email",
    "user_email",
    "invited_email",
  ]);
  if (!email) return null;

  const status =
    typeof entry.status === "string" ? entry.status.toLowerCase() : null;
  const isError = status === "error" || status === "failed";

  const errorMessage = isError
    ? (pickString(entry, ["error", "error_message", "detail", "message"]) ??
      "Failed to send invitation")
    : null;

  const shareId = pickString(entry, ["share_id", "id"]);

  return {
    email,
    isSuccess: !isError,
    errorMessage,
    shareId,
  };
}

function normalizeCreateSharesResponse(
  response: unknown,
): Array<CreateMeetingShareResult> {
  let list: Array<unknown> = [];
  if (Array.isArray(response)) {
    list = response;
  } else if (typeof response === "object" && response !== null) {
    const obj = response as Record<string, unknown>;
    for (const key of ["results", "items", "data", "shares"]) {
      if (Array.isArray(obj[key])) {
        list = obj[key];
        break;
      }
    }
  }

  if (import.meta.env.DEV && list.length > 0) {
    console.debug("[shares] POST response sample:", list[0]);
  }

  return list
    .map((entry) => normalizeCreateShareResult(entry))
    .filter((result): result is CreateMeetingShareResult => result !== null);
}

type MeetingSharesResponse = unknown;

const EMPTY_SHARE_PERMISSIONS: MeetingSharePermissions = {
  can_view_recording: false,
  can_view_transcription: false,
  can_view_analysis: false,
  can_view_summary: false,
};

function pickString(obj: Record<string, unknown>, keys: Array<string>) {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return null;
}

function normalizeShare(raw: unknown): MeetingShare | null {
  if (typeof raw !== "object" || raw === null) return null;
  const entry = raw as Record<string, unknown>;

  const id = pickString(entry, ["id", "share_id", "uuid"]);
  if (!id) return null;

  const nestedUser =
    typeof entry.user === "object" && entry.user !== null
      ? (entry.user as Record<string, unknown>)
      : null;
  const nestedRecipient =
    typeof entry.recipient === "object" && entry.recipient !== null
      ? (entry.recipient as Record<string, unknown>)
      : null;

  const email =
    pickString(entry, [
      "shared_email",
      "email",
      "recipient_email",
      "user_email",
      "shared_with_email",
      "invited_email",
    ]) ??
    (nestedUser ? pickString(nestedUser, ["email"]) : null) ??
    (nestedRecipient ? pickString(nestedRecipient, ["email"]) : null);

  if (!email) return null;

  const fullName =
    pickString(entry, [
      "shared_with_name",
      "full_name",
      "name",
      "recipient_name",
      "user_name",
    ]) ??
    (nestedUser ? pickString(nestedUser, ["full_name", "name"]) : null) ??
    (nestedRecipient
      ? pickString(nestedRecipient, ["full_name", "name"])
      : null);

  const rawPermissions =
    typeof entry.permissions === "object" && entry.permissions !== null
      ? (entry.permissions as Record<string, unknown>)
      : entry;

  const permissions: MeetingSharePermissions = {
    can_view_recording: Boolean(rawPermissions.can_view_recording),
    can_view_transcription: Boolean(rawPermissions.can_view_transcription),
    can_view_analysis: Boolean(rawPermissions.can_view_analysis),
    can_view_summary: Boolean(rawPermissions.can_view_summary),
  };

  return {
    id,
    email,
    full_name: fullName ?? null,
    permissions: {
      ...EMPTY_SHARE_PERMISSIONS,
      ...permissions,
    },
  };
}

function normalizeSharesResponse(
  response: MeetingSharesResponse,
): Array<MeetingShare> {
  let list: Array<unknown> = [];
  if (Array.isArray(response)) {
    list = response;
  } else if (typeof response === "object" && response !== null) {
    const obj = response as Record<string, unknown>;
    for (const key of ["items", "results", "shares", "data"]) {
      if (Array.isArray(obj[key])) {
        list = obj[key];
        break;
      }
    }
  }

  if (import.meta.env.DEV && list.length > 0) {
    console.debug("[shares] raw response sample:", list[0]);
  }

  return list
    .map((entry) => normalizeShare(entry))
    .filter((share): share is MeetingShare => share !== null);
}

export function meetingSharesQueryOptions(meetingBotId: string) {
  return queryOptions({
    queryKey: ["meetings", meetingBotId, "shares"],
    queryFn: async () => {
      const response = await api.processing.raw
        .get(`api/v1/meetings/${meetingBotId}/shares`)
        .json<MeetingSharesResponse>();
      return normalizeSharesResponse(response);
    },
  });
}

async function readHttpErrorDetail(error: unknown): Promise<string | null> {
  if (!(error instanceof HTTPError)) return null;
  try {
    const body = await error.response.json<{ detail?: unknown }>();
    if (typeof body.detail === "string") return body.detail;
    if (Array.isArray(body.detail)) {
      const messages = body.detail
        .map((item: unknown) => {
          if (typeof item === "string") return item;
          if (
            item &&
            typeof item === "object" &&
            "msg" in item &&
            typeof item.msg === "string"
          ) {
            return item.msg.replace(/^Value error,\s*/i, "");
          }
          return null;
        })
        .filter((message: unknown): message is string => Boolean(message));
      return messages.length > 0 ? messages.join(". ") : null;
    }
    return null;
  } catch {
    return null;
  }
}

export function useCreateMeetingSharesMutation(meetingBotId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      recipients: Array<CreateMeetingShareRecipient>,
    ): Promise<Array<CreateMeetingShareResult>> => {
      const response = await api.processing.raw
        .post(`api/v1/meetings/${meetingBotId}/shares`, {
          json: { recipients },
        })
        .json<unknown>();
      return normalizeCreateSharesResponse(response);
    },
    async onSuccess() {
      await queryClient.invalidateQueries({
        queryKey: ["meetings", meetingBotId, "shares"],
      });
    },
    async onError(error) {
      const detail = await readHttpErrorDetail(error);
      toast.error(detail ?? "Failed to send invitations");
    },
  });
}

export function useUpdateMeetingShareMutation(meetingBotId: string) {
  const queryClient = useQueryClient();
  const queryKey = ["meetings", meetingBotId, "shares"] as const;

  return useMutation({
    mutationFn: async ({
      shareId,
      permissions,
    }: {
      shareId: string;
      permissions: MeetingSharePermissions;
    }) => {
      await api.processing.raw.patch(
        `api/v1/meetings/${meetingBotId}/shares/${shareId}`,
        { json: { permissions } },
      );
    },
    async onMutate({ shareId, permissions }) {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Array<MeetingShare>>(queryKey);
      if (previous) {
        queryClient.setQueryData<Array<MeetingShare>>(
          queryKey,
          previous.map((share) =>
            share.id === shareId ? { ...share, permissions } : share,
          ),
        );
      }
      return { previous };
    },
    async onError(error, _vars, context) {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      const detail = await readHttpErrorDetail(error);
      toast.error(detail ?? "Failed to update permissions");
    },
    async onSettled() {
      await queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useRevokeMeetingShareMutation(meetingBotId: string) {
  const queryClient = useQueryClient();
  const queryKey = ["meetings", meetingBotId, "shares"] as const;

  return useMutation({
    mutationFn: async (shareId: string) => {
      await api.processing.raw.delete(
        `api/v1/meetings/${meetingBotId}/shares/${shareId}`,
      );
    },
    async onMutate(shareId) {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Array<MeetingShare>>(queryKey);
      if (previous) {
        queryClient.setQueryData<Array<MeetingShare>>(
          queryKey,
          previous.filter((share) => share.id !== shareId),
        );
      }
      return { previous };
    },
    onSuccess() {
      toast.success("Access revoked");
    },
    async onError(error, _shareId, context) {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      const detail = await readHttpErrorDetail(error);
      toast.error(detail ?? "Failed to revoke access");
    },
    async onSettled() {
      await queryClient.invalidateQueries({ queryKey });
    },
  });
}

const stateGroupsResponseSchema = z.record(z.string(), z.array(z.string()));
export type StateGroups = z.infer<typeof stateGroupsResponseSchema>;

export const stateGroupsQueryOptions = queryOptions({
  queryKey: ["meetings", "query-options"],
  queryFn: () =>
    api.processing.raw.get<StateGroups>("api/v1/meetings/state-groups").json(),
  staleTime: 1000 * 60 * 60 * 24, // 24 hrs
});
